import { useEffect, useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import type { FdeCopy, ProjectTask } from '@/frontend/features/Fde/types'

export type TaskPatch = Partial<Pick<ProjectTask, 'status' | 'priority' | 'task_category_id'>>
export interface PendingTaskMove {
  id: string
  status: ProjectTask['status']
  beforeId: string | null
}

export function useProjectTaskActions(projectId: string | undefined, copy: FdeCopy) {
  const { url } = usePage()
  const query = new URLSearchParams(url.split('?')[1] || '')
  const returnTo = url.split('?')[0] === fdeRoutes.tasks ? 'tasks' : 'management'
  const [busyId, setBusyId] = useState<string | null>(null)
  const [pendingMove, setPendingMove] = useState<PendingTaskMove | null>(null)
  const [notice, setNotice] = useState('')
  useEffect(() => {
    if (!notice) return
    const timer = window.setTimeout(() => setNotice(''), 2500)
    return () => window.clearTimeout(timer)
  }, [notice])

  function request(task: ProjectTask, suffix: string, payload: Record<string, unknown>) {
    if (!projectId || busyId) return
    router.patch(
      fdeProjectPath(`${fdeRoutes.projectTasks}/${task.id}${suffix}`, projectId, {
        return_to: returnTo,
        view: query.get('view'),
      }),
      payload as Parameters<typeof router.patch>[1],
      {
        preserveScroll: true,
        preserveState: true,
        onStart: () => setBusyId(task.id),
        onFinish: () => {
          setBusyId(null)
          setPendingMove(null)
        },
      },
    )
  }
  function patch(task: ProjectTask, changes: TaskPatch) {
    request(task, '', { project_task: { ...changes, lock_version: task.lock_version } })
  }
  function move(task: ProjectTask, status: ProjectTask['status'], beforeId: string | null) {
    if (busyId || !projectId) return
    // A pending projection gives immediate drag feedback; Rails remains authoritative.
    setPendingMove({ id: task.id, status, beforeId })
    request(task, '/move', {
      task_move: { status, before_task_id: beforeId, lock_version: task.lock_version },
    })
  }
  async function copyLink(task: ProjectTask) {
    if (!projectId) return
    try {
      await navigator.clipboard.writeText(
        new URL(
          fdeProjectPath(`${fdeRoutes.projectTasks}/${task.id}`, projectId),
          window.location.origin,
        ).href,
      )
      setNotice(copy.management.link_copied)
    } catch {
      setNotice(copy.management.copy_link_failed)
    }
  }
  return { busyId, pendingMove, notice, move, patch, copyLink }
}

export function projectTaskMoveProjection(tasks: ProjectTask[], move: PendingTaskMove | null) {
  if (!move) return tasks
  const task = tasks.find((item) => item.id === move.id)
  if (!task) return tasks
  const others = tasks.filter((item) => item.id !== move.id)
  const beforeIndex = move.beforeId ? others.findIndex((item) => item.id === move.beforeId) : -1
  others.splice(beforeIndex < 0 ? others.length : beforeIndex, 0, { ...task, status: move.status })
  return others
}
